import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from "react-redux";
import { Grid, Paper } from '@mui/material';
import { FacilityLevelOfCare, Estimate as NewEstimate, Vob as VobV3 } from '@finpay/estimation-types';
import moment from 'moment';
import _ from 'lodash';

import { RootState } from "../../../shared/state/root-reducer";
import { AppDispatch } from "../../../shared/state/store";
import { TypeOfCare, typeOfCare } from "../../../shared/model/type-of-care";
import {
    vobPatientPostBody,
    IocConfig,
    LaunchDataChange,
} from '../../models/patient';
import { Utils } from "../../../shared/utils";
import classes from "./Launch.module.css";
import QueryStringList from "./QueryStringList";
import { clearFinPassStatus, clearPatient, setPatient } from '../../state/vob-patient-slice';
import { clearFacilityPayers, clearVOB, setVOB } from '../../state/vob-slice';
import { clearEstimate, setEstimate } from "../../state/estimator-slice";
import { clearRiskAssessment } from '../../state/risk-assessment-slice';
import { checkClientPatientAccountId, checkClientPatientIOCId, getPatientDemographics, getPtRecord, savePatientPaymentProgram } from '../../../patient/state/patient-thunk';
import { patientService } from '../../../patient/services/patient-service';
import { getClientFacilities } from '../../../implementation-specialist/state/facility/implementation-facility-thunk';
import { createPatientIoc, getVobPatient, saveFinPassPatient, saveVobPatient } from '../../state/vob-patient-thunk';
import { EstimateRequestConfig, PayorLocRateLos } from '../../models/estimator';
import { callNewEstimateGet, callNewEstimatePost, callNewEstimatePut } from '../../state/estimator-thunk';
import { getEstimatorFacilityPayers, getVob, getVobClassifications, saveVob } from '../../state/vob-thunk';
import { configGetLOC, configGetPayers } from '../../../admin-configuration/state/admin-configuration-thunk';
import { showErrorStatus } from '../../../security/state/user-slice';
import { checkPermissions, checkUserRolePage } from '../../../security/components/access-control';
import { RolePageNames } from '../../../security/model/role-page-names';
import { UserRolePage } from '../../../security/model/user-role-page';
import { AxiosGetPayload } from '../../../shared/service/axios-get-payload';
import { crmInboundErrorMessages } from "../../../shared/enums";
import { ContactIncome, PhoneType, PrimaryContact } from '../../../shared/model/primary-contact';
import { PatientViewModel } from '../../../patient/components/models/patient-view-model';
import { admissionsAdvisorUtils } from '../../utils/admission-advisor-utils';
import { PayorRisk, TimingRisk, timingRiskNameLookup } from '../../../shared/model/timing-and-payor-risk';
import {FacilityPayerPlans, Vob, VobPostBody, emptyVob, emptyVobV3, patientNotes} from '../../models/vob';
import { PatientRecordRes, setSelectedPatientV2, clearEncounter, clearPatientIocStatus, setSelectedEncounter } from '../../../patient/state/patient-slice';
import { ClientCrm, ClientStatusCardViewModel, CrmObjectType } from '../../../shared/model/client-status-card';
import MobilePaymentsLoader from '../../../shared/components/mobile-payments-loader';
import {
  admissionsAdvisorService,
} from '../../services/admissions-advisor-services';
import { workFlowStatus } from 'src/patient/components/models/workflow-status';
import { ConfirmChangesModal } from './confirmChangesModal';
import { FacilityPayers } from '../../models/vob';
import { newPatientLaunch, patientRelaunch } from './launchV2Utils';

type ModalResponse = {
  confirmed: boolean;
};

const Launch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [debugMode, setDebugMode] = useState(false);
  const [queryParams, setQueryParams] = useState<any>();
  const [originalPtRecordResponse, setOriginalPtRecordResponse] = useState<any>();
  const [ptRecordResponse, setPtRecordResponse] = useState<any>();
  const [clientsLeadId, setClientsLeadId] = useState<any>();
  const [clientsAccountId, setClientsAccountdId] = useState<any>();
  const [currentClientId, setCurrentClientId] = useState<any>();
  const [confirmChangesModalOpen, setConfirmChangesModalOpen] = useState(false);
  const [modalCallback, setModalCallback] = useState<(result: ModalResponse) => void>(() => {});
  const [changeSummary, setChangeSummary] = useState<LaunchDataChange>({
    facilityChange: {
      hasChanged: false,
      prevId: 0,
      prevName: '',
      newId: 0,
      newName: '',
    },
    payerChange: {
      hasChanged: false,
      prevId: 0,
      prevName: '',
      newId: 0,
      newName: '',
      ptRecordName: '',
    },
    clientId: 0
  });
  const isLaunchInit = useRef(false);
  const leadDataLoaded = useRef(false);
  const iOCLoaded = useRef(false);
  const isStatePrefilled = useRef(false);
  const paramId: number = -2;
  const AVG_OON_PAYOR_ID = 14;
  const AVG_OON_PAYOR_PLAN_ID = 33;

  const clientsPatientIOCIdFilter = useSelector((state: RootState) => {
    return state?.patientContext.clientsPatientIOCIdFilter;
  });

  const patientRecordInfo = useSelector((state: RootState) => {
    return state?.patientContext.inboundPtRecord;
  });

  const payers = useSelector((state: RootState) => {
    return state.adminContext.adminConfigurationContext?.payers;
  });

  const vobState = useSelector((state: RootState) => {
    return state.admissionsAdvisorContext.vobContext;
  });

  const estimate = useSelector((state: RootState) => {
    return state.admissionsAdvisorContext.estimatorContext?.estimate;
  });

  const allClients: ClientStatusCardViewModel[] = useSelector((state: RootState) => {
    return state.implementationContext.implementationSpecialistClient.allClients;
  });

  const userProfile = useSelector((state: RootState) => {
    return state.userContext.userProfile;
  });

  const clientsPatientAccountIdFilter = useSelector((state: RootState) => {
    return state?.patientContext.clientsPatientAccountIdFilter;
  });

  const vobClassificationsState = useSelector((state: RootState) => {
    return state.admissionsAdvisorContext.vobContext?.vobClassifications;
  });

  const facilities = useSelector((state: RootState) => {
    return state.implementationContext.implementationFacility.facilities;
  });

  const allClientsMap = useSelector((state: RootState) => {
    return state.implementationContext.implementationSpecialistClient.allClientsMap;
  });

  const getPtRecordData = async (
    clientsPatientIOCId: string,
    clientId: number
  ) => {
    leadDataLoaded.current = true;
    const clientItem: ClientStatusCardViewModel = allClients.find((client:ClientStatusCardViewModel) =>  client.clientId === clientId)!
    let clientCrm: ClientCrm[] = clientItem?.clientCrm!;
    if (clientItem && !clientItem?.isActive){
      return handleGenericError(crmInboundErrorMessages.inactiveClient)
    }
    // Temporarily defaulting CRM to SalesForce if it's not in clientInfo
    if (!clientCrm || clientCrm?.length === 0 ) {
      clientCrm = [
        {
          preadmitCrmObjectType: {} as CrmObjectType,
          admitCrmObjectType: {} as CrmObjectType,
          crmType: {
            crmName: "Salesforce",
            crmTypeSlug: "salesforce",
          }
        },
      ];
    }
    
    const ptRecordRes: AxiosGetPayload = await dispatch(
      getPtRecord({ leadId: clientsPatientIOCId, clientId: clientId, crm: clientCrm[0] })
    );
    let errorMessage: string = crmInboundErrorMessages.misconfigured;
    if (ptRecordRes.error) {
      if (
        ptRecordRes.meta.arg.leadId === undefined ||
        ptRecordRes.payload === undefined
      ) {
        errorMessage = crmInboundErrorMessages.patientError;
      } else if (Number.isNaN(ptRecordRes.meta.arg.clientId)) {
        errorMessage = crmInboundErrorMessages.facilityError;
      }
      dispatch(showErrorStatus(errorMessage));
      navigate(`/admissions-advisor`);
    } else if (typeof ptRecordRes.payload === "string") {
      if (ptRecordRes.payload === "No facility to process.") {
        errorMessage = crmInboundErrorMessages.facilityError;
      }
      dispatch(showErrorStatus(errorMessage));
      navigate(`/admissions-advisor`);
    }
  };

  const checkExistingIOC = async (patientRecordInfo: PatientRecordRes) => {
      await dispatch(configGetPayers(paramId));
      await dispatch(getVobClassifications(paramId));
      dispatch(configGetLOC(paramId))
      const getFacilitiesRespone: any = await dispatch(
          getClientFacilities(currentClientId)
      );
      if(getFacilitiesRespone.error) {
        return navigate('/accessdenied');
      }

      if (
          patientRecordInfo.facilityId === null ||
          patientRecordInfo.facilityId === undefined
      ) {
          let buildingPatientRecord: any = Utils.deepClone(patientRecordInfo);
          buildingPatientRecord.facilityId =
              getFacilitiesRespone.payload?.groupedFacilities[0][0].facilityId;
          setPtRecordResponse(buildingPatientRecord);
      }
      if (patientRecordInfo.clientsPatientIOCId) {
          const keySearchPayload = {
              clientPatientIOCId: patientRecordInfo.clientsPatientIOCId,
              clientId: currentClientId,
          };
          await dispatch(checkClientPatientIOCId(keySearchPayload));
          iOCLoaded.current = true;
      }
  };

  const checkExistingClientAccountId = async () => {
      const ptRecord = Utils.deepClone(ptRecordResponse);
      if (ptRecord.clientsPatientAccountId) {
          const keySearchPayload = {
              clientsPatientAccountId: ptRecord.clientsPatientAccountId,
              clientId: currentClientId,
          };
          dispatch(checkClientPatientAccountId(keySearchPayload));
          iOCLoaded.current = true;
      } else {
        return handleGenericError(crmInboundErrorMessages.missingPatientAccountId);
      }
  };

  const getSelfPayPayer = (facilityPayers: any) => {
      return facilityPayers?.payload?.find((payer: any) => payer.cfgPayorId === 97);
  };

  const getPlanDetails = (selfPayPayer: any, facilityPayers: any) => {
      const [plan] = selfPayPayer?.facilityPayorPlans || [];
      return {
          ...plan,
          payorPlanId: plan?.cfgPayorPlanId,
          planName: plan?.facilityPayorPlanName,
          planCode: plan?.facilityPayorPlanCode,
          facilityPlanId: plan?.facilityPayorPlanId,
          pdrRate: (plan?.facilityPayorLocRateLos as PayorLocRateLos)?.pdrRate,
          losDays: (plan?.facilityPayorLocRateLos as PayorLocRateLos)?.losDays,
          payersPlanList: facilityPayers.payload,
      };
  };

  function mapToPatientState(patientDetails: any, ptRecord: any): vobPatientPostBody {
    return {
      firstName: patientDetails?.contact?.firstName,
      lastName: patientDetails?.contact?.lastName,
      birthDate: patientDetails?.contact?.birthDate,
      fpPatientId: patientDetails?.patientId,
      middleName: patientDetails?.contact.middleName || ptRecord?.patient?.contact.middleName,
      advisorPatientBody: {
        email: patientDetails?.contact?.email,
        primaryAddress: patientDetails?.contact?.primaryAddress,
        phoneNumber: patientDetails?.contact?.primPhoneNum,
      },
      clientId: ptRecord?.clientId,
    };
  }
  
  function formatVOBComments(ptRecordClone: any): patientNotes[] | undefined {
      return ptRecordClone.vob?.comments?.length
          ? ptRecordClone.vob?.comments.map((item: any) => ({
                noteText: item.comment,
                noteDt: '',
            }))
          : undefined;
  }

  function mapToIOCModel(
    vobPatientRecord: any,
    estimatorState: any,
    patientId: number,
    estimateId: number,
    ptRecord: any,
    queryParams: any
  ): IocConfig {
    const ptRecordTimingRiskId = ptRecord.timingRisk?.timingRiskId || 1
    const currentTimingRisk: TimingRisk = {
      timingRiskId: ptRecordTimingRiskId,
      timingRiskName: timingRiskNameLookup(ptRecordTimingRiskId)!
    };
    const emptyPayorRisk: PayorRisk = {
      payorRiskId: 1,
      payorRiskName: "",
    };
    let currentTypeOfCare: TypeOfCare | undefined = undefined;
    if (ptRecord.typeOfCare?.typeOfCareId > 0) {
      currentTypeOfCare = typeOfCare.find(
          item => item.typeOfCareId === ptRecord.typeOfCare?.typeOfCareId
      );
    }
    const urlParams = new URLSearchParams(queryParams)
    const formedParams = Object.fromEntries(urlParams)
    let wfStatus;
    const iocRoleStatus = hasIOCRole();
    const estimateRoleStatus = hasFinAdviorRole();
    if (formedParams.dst === 'fp' || (!estimateRoleStatus && iocRoleStatus)){
      wfStatus = workFlowStatus.preconvert.find((workflow: any)=>{
              return workflow.workflowStatus.workflowStatusName === "New" && workflow.workflowSubStatus.workflowSubStatusName === "None"
            }) 
    } else {
      wfStatus = {
        workflowId: 2,
        workflowStatus: {
          workflowStatusId: 20,
        },
        workflowSubStatus: {
          workflowSubStatusId: 7,
        },
      }
    }
    const pfrAmt = 
      formedParams.dst === 'fp' && ptRecord.hasOwnProperty('pfr')
        ? ptRecord.pfr 
        : estimatorState?.summary?.totalPFR ?? 0;
    const iocConfig: IocConfig = {
      paramId: 0,
      patientId: patientId,
      clientId: vobPatientRecord.vob.client.clientId!,
      facilityId: vobPatientRecord.vob.facility.facilityId!,
      pfrAmt: pfrAmt,
      timingRisk: currentTimingRisk,
      payorRisk: emptyPayorRisk,
      clientsPatientIOCId: clientsLeadId,
      clientsPatientAccountId: clientsAccountId,
      pfrEstimateId: estimateId,
      patientInsurance: ptRecord.patientInsurance,
      admissionDt: ptRecord.admissionDt,
      dischargeDt: ptRecord.dischargeDt,
      ...(currentTypeOfCare && { typeOfCare: currentTypeOfCare}),
      // temporarily hardcoding workflow status
      workflow: wfStatus
    };
    return iocConfig;
  }

  function mapToPatientViewModel(
    vobPatientRecord: vobPatientPostBody,
    patientRecord: any
  ): PatientViewModel {
    const patientContact: PrimaryContact = {
      title: patientRecord.patient?.contact?.title || undefined!,
      firstName: vobPatientRecord.firstName,
      middleName: patientRecord.patient?.contact?.middleName || vobPatientRecord.middleName,
      lastName: vobPatientRecord.lastName,
      primaryAddress: {
        streetAddress1:
          vobPatientRecord.advisorPatientBody?.primaryAddress?.streetAddress1!,
        streetAddress2:
          vobPatientRecord.advisorPatientBody?.primaryAddress?.streetAddress2!,
        city: vobPatientRecord.advisorPatientBody?.primaryAddress?.city!,
        state: {
          stateId:
            vobPatientRecord.advisorPatientBody?.primaryAddress?.state
              ?.stateId!,
          stateCode:
            vobPatientRecord.advisorPatientBody?.primaryAddress?.state
              ?.stateCode!,
          stateName:
            vobPatientRecord.advisorPatientBody?.primaryAddress?.state
              ?.stateName!,
        },
        zipCode: vobPatientRecord.advisorPatientBody?.primaryAddress?.zipCode!,
        addressType: patientRecord.patient?.contact?.primaryAddress?.addressType || "Primary",
      },
      email: vobPatientRecord.advisorPatientBody?.email!,
      primPhoneNum: vobPatientRecord.advisorPatientBody?.phoneNumber!,
      primPhoneType: patientRecord.patient?.contact?.primPhoneType || PhoneType.home,
      secPhoneNum: patientRecord.patient?.contact?.secPhoneNum,
      secPhoneType: patientRecord.patient?.contact?.secPhoneType || PhoneType.mobile,
      leaveVMFl: patientRecord.patient?.contact?.leaveVMFl || false,
      smsConsentFl: patientRecord.patient?.contact?.smsConsentFl || false,
      ssnLast4: patientRecord.patient?.contact?.ssnLast4 || "",
      birthDate: patientRecord.patient?.contact?.birthDate || vobPatientRecord.birthDate,
      contactIncome: {
        isEmployed: patientRecord.patient?.contact?.contactIncome?.isEmployed || false,
        yearlySalary: patientRecord.patient?.contact?.contactIncome?.yearlySalary || 0,
        employer: patientRecord.patient?.contact?.contactIncome?.employer ||  "",
        yearsAtEmployer: patientRecord.patient?.contact?.contactIncome?.yearsAtEmployer || 0,
      } as ContactIncome,
    };

    const newPatient = {
      contact: patientContact,
      patientStatus: "NEW",
      clientId: patientRecord.clientId.toString(),
    } as PatientViewModel;

    return newPatient;
  }

  const setNewPatientRecord = async (existingPatient: boolean, existingPatientInfo: any | undefined, existingURLPath: string) => {
    let redirectURLPath: string = existingURLPath;
    const ptRecord = Utils.deepClone(ptRecordResponse);
    const urlParams = new URLSearchParams(queryParams);
    let formedParams = Object.fromEntries(urlParams);
    const combinedParams = {
      ...ptRecord,
      op: formedParams.op ? formedParams.op : undefined,
    };

    let clonedVob = Utils.deepClone(vobState);
    let clonedEstimate: NewEstimate | Record<string, never> = estimate ? Utils.deepClone(estimate) : {};
    let patientState: vobPatientPostBody;
    let currentPatientId: number;
    let facilityPayers: any;
    let payer = emptyVob.payer;
    let plan = emptyVob.plan;
    formedParams.inboundPatient = "new";

    if (!existingPatient) {
      patientState = {
        firstName: ptRecord.patient?.contact?.firstName
          ? ptRecord.patient?.contact?.firstName
          : "",
        lastName: ptRecord.patient?.contact?.lastName
          ? ptRecord.patient?.contact?.lastName
          : "",
        birthDate: ptRecord.patient?.contact?.birthDate
          ? ptRecord.patient?.contact?.birthDate
          : "",
        fpPatientId: undefined,
        middleName: ptRecord.patient?.contact?.middleName,
        advisorPatientBody: {
          email: ptRecord.patient?.contact?.email,
          primaryAddress: ptRecord.patient?.contact?.primaryAddress,
          phoneNumber: ptRecord.patient?.contact?.primPhoneNum,
        },
        clientId: ptRecord.clientId,
      };
      // check for required patientient information before creating Patient records
      if (
        !patientState.firstName ||
        !patientState.lastName ||
        !patientState.birthDate
      ) {
       return handleGenericError(crmInboundErrorMessages.patientInfoError);
      } else {
        // Save Advisor Patient
        const aAPatientResponse: any = await dispatch(saveVobPatient(patientState));
        patientState.advisorPatientId = aAPatientResponse.payload.advisorPatientId;
    
        // Save FinPass Patient
        const newFinPassPatientPayload = mapToPatientViewModel(patientState, ptRecord);
        const fpPatientResponse = await dispatch(
          saveFinPassPatient({
            patient: newFinPassPatientPayload,
            advisorPatientId: aAPatientResponse.payload.advisorPatientId,
            isAddNew: true,
            patientId: undefined,
          })
        );
        dispatch(setSelectedPatientV2(fpPatientResponse?.payload)); // setting main FinPass Patient State to prefill Complete Patient Record
        patientState.fpPatientId = fpPatientResponse?.payload?.patientId;
        currentPatientId = fpPatientResponse?.payload?.patientId;
      }
    } else {
      patientState = {
        firstName: existingPatientInfo?.contact?.firstName,
        lastName: existingPatientInfo?.contact?.lastName,
        birthDate: ptRecord.patient?.contact?.birthDate || existingPatientInfo?.contact?.birthDate,
        fpPatientId: existingPatientInfo?.patientId,
        middleName: existingPatientInfo?.contact?.middleName || ptRecord.patient?.contact?.middleName,
        advisorPatientBody: {
          email: ptRecord.patient?.contact?.email,
          primaryAddress: ptRecord.patient?.contact?.primaryAddress,
          phoneNumber: ptRecord.patient?.contact?.primPhoneNum
        },
        advisorPatientId: existingPatientInfo.advisorPatientId,
        clientId: ptRecord?.clientId,
      };
      currentPatientId = existingPatientInfo.patientId;
    }

    // If the ptRecord Estimate contains LOCs we need gather the Classification Ids and get them matched to the master list
    if (ptRecord.estimate?.loc?.length > 0) {
        // 1. Get all classification IDs from ptRecord Estimate LOCs
        const ptRecordLOCClassificationIds = ptRecord.estimate.loc
            .map((item: any) => item.vobClassificationId)
            .filter((id: any) => id !== undefined && id !== null);
        
        // 2. Determine existing VOB classifications based on inNetwork flag
        const isInNetwork = ptRecord.vob?.payer?.inNetwork ?? false;

        // Grab any classifications on the ptRecord VOB
        const existingVobClassifications = isInNetwork
          ? ptRecord.vob?.inNetwVobClassifications
          : ptRecord.vob?.ooNetwVobClassifications;

        // 3. Filter out classification IDs already in the VOB classification list
        const newIdsToAdd = ptRecordLOCClassificationIds.filter(
          (id: number) =>
            !existingVobClassifications.some(
              (obj: { vobClassificationId: any }) => obj.vobClassificationId === id
            )
        );

        // 4. Map new IDs into classification objects
        const newObjectsToAdd = newIdsToAdd.map((id: number) => ({ vobClassificationId: id }));

        if (!ptRecord.vob) {
          ptRecord.vob = {};
        }
  
        if (!Array.isArray(ptRecord.vob?.inNetwVobClassifications)) {
          ptRecord.vob.inNetwVobClassifications = [];
        }
        
        if (!Array.isArray(ptRecord.vob?.ooNetwVobClassifications)) {
          ptRecord.vob.ooNetwVobClassifications = [];
        }

        // 5. Append to the correct classification list
        if (isInNetwork) {
          ptRecord.vob.inNetwVobClassifications.push(...newObjectsToAdd);
        } else {
          ptRecord.vob.ooNetwVobClassifications.push(...newObjectsToAdd);
        }
    }

    const ptRecordVOB = admissionsAdvisorUtils.matchClassifications(ptRecord.vob, vobClassificationsState);

    const patientInsurance = ptRecord.patientInsurance?.[0];

    clonedVob.vob = admissionsAdvisorUtils.mapVOBs(clonedVob.vob, ptRecordVOB, patientInsurance)

    clonedVob.vob.client.clientId = ptRecord.clientId
      ? ptRecord.clientId
      : currentClientId;

    clonedVob.vob.facility.facilityId = ptRecord.facilityId
      ? ptRecord.facilityId
      : undefined;

    clonedVob.vob.patientNotes = formatVOBComments(ptRecord);

    if (ptRecord.facilityId) {
      const sssconfig: any = {
        paramId: paramId,
        masterListPayers: payers,
        facilityId: ptRecord.facilityId,
      };
      facilityPayers = await dispatch(getEstimatorFacilityPayers(sssconfig));
    }

    let matchingFacilityPayerByCode: any;

    if (ptRecord.vob?.payer?.clientCrmPayerCode && ptRecord.facilityId) {
        matchingFacilityPayerByCode = findMatchingPayerByCode(
            ptRecord.vob.payer.clientCrmPayerCode,
            facilityPayers.payload
        );
    }

    if (matchingFacilityPayerByCode) {
        payer = {
            ...matchingFacilityPayerByCode,
            payorId: matchingFacilityPayerByCode.cfgPayorId,
            payorName: matchingFacilityPayerByCode.facilityPayorName,
            payorCode: matchingFacilityPayerByCode.facilityPayorCode,
            inNetwork: matchingFacilityPayerByCode.inNetwork,
            payersList: matchingFacilityPayerByCode.facilityPayorPlans,
        };
        const planCodeOrName =
            ptRecord.vob?.plan?.clientCrmPlanCode ||
            ptRecord.vob?.plan?.planName;
        const matchingPayorPlan = findMatchingPlan(
            planCodeOrName,
            matchingFacilityPayerByCode.facilityPayorPlans
        );

        if (matchingPayorPlan) {
            plan = {
                ...matchingPayorPlan,
                payorPlanId: matchingPayorPlan.cfgPayorPlanId,
                planName: matchingPayorPlan.facilityPayorPlanName,
                planCode: matchingPayorPlan.facilityPayorPlanCode,
                facilityPlanId: matchingPayorPlan.facilityPayorPlanId,
                pdrRate: (matchingPayorPlan.facilityPayorLocRateLos as PayorLocRateLos)
                    ?.pdrRate,
                losDays: (matchingPayorPlan?.facilityPayorLocRateLos as PayorLocRateLos)
                    ?.losDays,
                payersPlanList: facilityPayers.payload,
            };
        }
    } else if (ptRecord.vob?.payer?.payerName) {
        const matchingFacilityPayerByName = findMatchingPayerByName(
            ptRecord.vob.payer.payerName,
            facilityPayers.payload
        );

        if (matchingFacilityPayerByName) {
            payer = {
                ...matchingFacilityPayerByName,
                payorId: matchingFacilityPayerByName.cfgPayorId,
                payorName: matchingFacilityPayerByName.facilityPayorName,
                payorCode: matchingFacilityPayerByName.facilityPayorCode,
                inNetwork: matchingFacilityPayerByName.inNetwork,
                payersList: matchingFacilityPayerByName.facilityPayorPlans,
            };

            const planCodeOrName =
                ptRecord.vob?.plan?.clientCrmPlanCode ||
                ptRecord.vob?.plan?.planName;
            const foundPayerPlan = findMatchingPlan(
                planCodeOrName,
                matchingFacilityPayerByName.facilityPayorPlans
            );

            if (foundPayerPlan) {
                plan = {
                    ...foundPayerPlan,
                    payorPlanId: foundPayerPlan.cfgPayorPlanId,
                    planName: foundPayerPlan.facilityPayorPlanName,
                    planCode: foundPayerPlan.facilityPayorPlanCode,
                    facilityPlanId: foundPayerPlan.facilityPayorPlanId,
                    pdrRate: (foundPayerPlan.facilityPayorLocRateLos as PayorLocRateLos)
                        ?.pdrRate,
                    losDays: (foundPayerPlan?.facilityPayorLocRateLos as PayorLocRateLos)
                        ?.losDays,
                    payersPlanList: facilityPayers.payload,
                };
            }
        }
    }

    if (!payer.payorId) {
      let payerLookup = facilityPayers?.payload?.find(
        (el: any) => el.cfgPayorId === AVG_OON_PAYOR_ID
      )!;
      if (payerLookup) {
        payer = {
          ...payerLookup,
          payorId: payerLookup.cfgPayorId,
          payorName: payerLookup.facilityPayorName,
          payorCode: payerLookup.facilityPayorCode,
          inNetwork: payerLookup.inNetwork,
          payersList: payerLookup.facilityPayorPlans,
        };
        let planToSave: FacilityPayerPlans | null;

     
        if (!plan.payorPlanId) {
          planToSave = payerLookup.facilityPayorPlans?.find(
            (el: FacilityPayerPlans) => el.cfgPayorPlanId === AVG_OON_PAYOR_PLAN_ID
          )!;
       
          plan = {
            ...planToSave,
            payorPlanId: planToSave?.cfgPayorPlanId,
            planName: planToSave?.facilityPayorPlanName,
            planCode: planToSave?.facilityPayorPlanCode,
            facilityPlanId: planToSave?.facilityPayorPlanId,
            pdrRate: (planToSave?.facilityPayorLocRateLos as PayorLocRateLos)
              ?.pdrRate,
            losDays: (planToSave?.facilityPayorLocRateLos as PayorLocRateLos)
              ?.losDays,
            payersPlanList: facilityPayers.payload,
          };
        }
      }
    }
    
    // setting self-pay if insuranceType is set on ptRecord
    if (
      ptRecord?.patient?.insurance?.insuranceType === 'self-pay' &&
        (!ptRecord?.vob || Object.keys(ptRecord.vob).length === 0)
    ) {
        const selfPayPayer = getSelfPayPayer(facilityPayers);
        if (selfPayPayer) {
            payer = {
                ...selfPayPayer,
                payorId: selfPayPayer.cfgPayorId,
                payorName: selfPayPayer.facilityPayorName,
                payorCode: selfPayPayer.facilityPayorCode,
                inNetwork: selfPayPayer.inNetwork,
                payersList: selfPayPayer.facilityPayorPlans,
            };
            plan = getPlanDetails(selfPayPayer, facilityPayers);
        }
    }

    clonedVob.vob.payer = payer;
    clonedVob.vob.plan = plan;

    let vobPostBody: VobPostBody;
    // self pay vob sets is valid to true, and self pay to true
    // resets the policy dates as well
    // sets fields to 0 and empties out the vob classifications
    if(clonedVob.vob.payer.cfgPayorId === 97) {
        vobPostBody = admissionsAdvisorUtils.createSelfPayVob(
            patientState.advisorPatientId!,
            clonedVob.vob,
            vobClassificationsState[0]
        );
    } else {
        vobPostBody = {
            advisorPatientId: patientState.advisorPatientId,
            fpClientId: ptRecord.clientId,
            fpClientFacilityId: ptRecord.facilityId,
            vobBody: {
                ...clonedVob.vob
            },
        };
    }
    const vobResponse = await dispatch(saveVob(vobPostBody)); 
    clonedVob.vob = vobResponse?.payload.vobBody;
    clonedEstimate.anticipatedAdmitDate = ptRecord.admissionDt
      ? ptRecord.admissionDt
      : null;

    const today = Utils.convertISODateAsUTC(new Date().toISOString());
    clonedEstimate.description = `No LOC - ${today}`;

    const postEstimateBody = admissionsAdvisorUtils.mapToNewEstimate(
      vobResponse?.payload.vobId!,
      ptRecord.clientId,
      ptRecord.facilityId,
      clonedEstimate,
      patientState.advisorPatientId!,
    );

    const updatedPostEstimateBody = _.omit(postEstimateBody, ['priorCare', 'financialAssistance']);

    // if we have everything we need to populate loc
    // fetch facility locs by the plan id, exactly like we do in the estimator
    // check the names of the loc provided by the patient record call
    // set the facilityLevelOfCare in the estimate
    if(plan.facilityPlanId && ptRecord.estimate?.loc && ptRecord.estimate?.loc?.length > 0) {
      const response = await admissionsAdvisorService.getFacilityLevelsOfCare(ptRecord.facilityId, plan.facilityPlanId);

      const facilityLevelOfCare: FacilityLevelOfCare[] = (response.entity as FacilityLevelOfCare[])
        ?.filter((loc: FacilityLevelOfCare) => {
          return ptRecord.estimate?.loc?.some((crmLoc: any) => {
            return crmLoc.locName === loc.facilityLevelOfCareName;
          });
        });

        // Ensure the VOB includes all classification IDs from each Level of Care in the estimate.
        // Step 1: Extract all classification IDs from the estimate's LOCs.
        const LOCClassificationIds: number[] = facilityLevelOfCare
            .map(item => item.vobClassificationId)
            .filter((id): id is number => id !== undefined && id !== null);

        // 2. Take all of these LOCClassificationIds and match them in the list facility classification
        const fullMatchedClassifications =
            admissionsAdvisorUtils.matchClassificationsById(
                LOCClassificationIds,
                vobClassificationsState
            );
        // 3. Determine network status
        const isInNetwork = clonedVob.vob?.payer?.inNetwork ?? false;

        // 4. Grab existing classifications
        const existingVobClassifications = isInNetwork
            ? clonedVob.vob?.inNetwVobClassifications || []
            : clonedVob.vob?.ooNetwVobClassifications || [];

        // 5. Get IDs of existing classifications to avoid duplicates
        const existingIds = new Set(
            existingVobClassifications.map(
                (id: {vobClassificationId: number}) => id.vobClassificationId
            )
        );

        // Filter new ones not already in the existing list
        const newClassificationsToAdd = fullMatchedClassifications.filter(
            (c: any) => !existingIds.has(c.vobClassificationId)
        );
        const updatedVobClassifications = [
            ...existingVobClassifications,
            ...newClassificationsToAdd,
        ];

        // 6. Assign back to VOB and save
        const updatedVob = {
            ...clonedVob.vob,
            ...(isInNetwork
                ? {inNetwVobClassifications: updatedVobClassifications}
                : {ooNetwVobClassifications: updatedVobClassifications}),
        };
        const updatedVobPostBody = {
            advisorPatientId: patientState.advisorPatientId,
            fpClientId: ptRecord.clientId,
            fpClientFacilityId: ptRecord.facilityId,
            vobBody: {
                ...updatedVob,
            },
        };
        await dispatch(saveVob(updatedVobPostBody));
        if (ptRecord?.vob) {
          dispatch(setVOB(clonedVob.vob));
        }

        updatedPostEstimateBody.facilityLevelOfCare = facilityLevelOfCare;

      const levelsOfCare = updatedPostEstimateBody?.facilityLevelOfCare
      ?.map((loc: FacilityLevelOfCare) => loc.facilityLevelOfCareName)
      .join(', ');

      const estimateDescription = `${levelsOfCare} - ${today}`;

      updatedPostEstimateBody.description = estimateDescription;
    }

    const savedEstimateResponse = await dispatch(
        callNewEstimatePost(updatedPostEstimateBody)
    );

    // @ts-ignore
    if (savedEstimateResponse?.error?.message) {
      return handleGenericError(crmInboundErrorMessages.saveEstimateError);
    }

    dispatch(setEstimate(savedEstimateResponse?.payload));

    // Save Instance of Care
    const iocConfig = mapToIOCModel(
      clonedVob,
      savedEstimateResponse?.payload,
      currentPatientId,
      savedEstimateResponse?.payload?.estimateId,
      ptRecord,
      queryParams
    );

    patientState.birthDate = moment.utc(patientState.birthDate).format('YYYY-MM-DD'); // adjusting birthdate for vob input field

    const iocResponse: any = await dispatch(createPatientIoc(iocConfig));

    if (ptRecord?.vob) {
      dispatch(setVOB({
        ...clonedVob.vob,
        urlParams: combinedParams
      }));
    }

    if(iocResponse?.payload?.patientEncounterId) {
      const putEstimateBody = admissionsAdvisorUtils.mapToNewEstimate(
        vobResponse?.payload.vobId!,
        ptRecord.clientId,
        ptRecord.facilityId,
        savedEstimateResponse?.payload,
        patientState.advisorPatientId!,
        iocResponse?.payload?.patientEncounterId
      );

      const putEstimateResponse = await dispatch(callNewEstimatePut({estimate: putEstimateBody, estimateId: savedEstimateResponse?.payload?.estimateId}));

      // @ts-ignore
      if (putEstimateResponse?.error?.message) {
        return handleGenericError(crmInboundErrorMessages.saveEstimateError);
      }
    
      await dispatch(setEstimate(putEstimateResponse?.payload));
    }
    
    dispatch(setPatient({...patientState}));

    dispatch(setSelectedEncounter(iocResponse?.payload));

    dispatch(clearPatientIocStatus());

    dispatch(clearFinPassStatus());

    const estimasteRoleStatus = hasFinAdviorRole();
    if (redirectURLPath === "specialistDashboard" || !estimasteRoleStatus) {
      redirectURLPath = `/specialist/dashboard/${currentPatientId}`;
    }
    navigate(redirectURLPath);
  };

  const handleGenericError = async (errorMessage: string) => {
    await dispatch(showErrorStatus(errorMessage));
    navigate(`/admissions-advisor`);
  };

  const clearAAState = () => {
      dispatch(clearPatient());
      dispatch(clearVOB());
      dispatch(clearEstimate());
      dispatch(clearFacilityPayers());
      dispatch(clearRiskAssessment());
  };

  const findMatchingPayerByCode = (code: string, payerList: any): any | undefined =>
      payerList.find((payer: { facilityPayorCode: string; }) => payer.facilityPayorCode === code);

  const findMatchingPayerByName = (name: string, payerList: any[]): any | undefined =>
    payerList.find((payer) => new RegExp(name, 'i').test(payer.facilityPayorName));

  const findMatchingPlan = (codeOrName: string, planList: any[]): any | undefined =>
      planList.find((plan) => plan.facilityPayorPlanCode === codeOrName || new RegExp(codeOrName, 'i').test(plan.facilityPayorPlanName));
    
  const getPatientEncounter = async (clientsPatientIOCIdFilter: number) => {
    let redirectURLPath: string = "/admissions-advisor";
    let ptRecordClone = Utils.deepClone(ptRecordResponse);
    const urlParams = new URLSearchParams(queryParams);
    let formedParams = Object.fromEntries(urlParams);
    const combinedParams = {
      ...ptRecordClone,
      op: formedParams.op ? formedParams.op : undefined,
    };
    const estimasteRoleStatus = hasFinAdviorRole();
    const instanceOfCare = await patientService.getPatientInstanceOfCare({
      patientId: 0,
      encounterId: clientsPatientIOCIdFilter,
    });
    let getAttachedEstimate: any;
    let vobResult: any;
    if (formedParams.op === "pay") {
      if(instanceOfCare.entity.workflow.workflowStatus.workflowStatusDesc === "Not Managed") {
        if (!estimasteRoleStatus) {
          const iocRoleStatus = hasIOCRole();
          if (iocRoleStatus) {
              redirectURLPath = `/specialist/dashboard/${instanceOfCare.entity.patientId}`;
          } else {
              redirectURLPath = "/accessdenied";
          }
        }
      } else {
        redirectURLPath = `/specialist/dashboard/${instanceOfCare.entity.patientId}`;
      }
    }

    if (instanceOfCare.entity.pfrEstimateId) {
      const config: EstimateRequestConfig = {
        paramId: paramId,
        estimationId: instanceOfCare.entity.pfrEstimateId,
      };

      getAttachedEstimate = await dispatch(callNewEstimateGet(config));

      if (getAttachedEstimate.error?.message) {
        return handleGenericError(crmInboundErrorMessages.newEstimateIdError);
      }

      if(clientsPatientIOCIdFilter) {
        dispatch(setEstimate({ ...getAttachedEstimate.payload, patientEncounterId: clientsPatientIOCIdFilter}))
      }
      
      if (getAttachedEstimate.payload.vobId) {
        const vobConfig: any = {
          paramId: paramId,
          vobId: getAttachedEstimate.payload.vobId,
        };
        vobResult = await dispatch(getVob(vobConfig));
      }
    }

    let selectedIoc = instanceOfCare.entity;
    let facilityId = selectedIoc.facilityId || ptRecordClone.facilityId;
    ptRecordClone.inboundPatient = undefined;

    const sssconfig: any = {
      paramId: paramId,
      masterListPayers: payers,
      facilityId: facilityId,
    };

    const facilityPayers = await dispatch(getEstimatorFacilityPayers(sssconfig)); //facilityPayers

    let savedClientId = currentClientId;
    let handleChange = false;
    let isConverted = false;
    let hasCRMChanged =  {
      facilityChange: false,
      payerChange: false
    }
    let confirmPtChanges = false;
    // Only do the changes check - if a Facility ID is present. 
    // Checking to see if there are changes in Facility or Payer from ptRecord vs Save VOB
    const ptRecordValid = !!(
      originalPtRecordResponse?.facilityId ||
      originalPtRecordResponse?.vob?.payer?.payerName
    );
    
    let facilityPayersResponse: any = [];
    const newFacilityId = originalPtRecordResponse?.facilityId ?? vobResult?.payload?.fpClientFacilityId;

    if (ptRecordValid) {
      const adjustedFacility = 
         originalPtRecordResponse?.facilityId ?? vobResult?.payload?.fpClientFacilityId;
      const adjustedPayer = 
        originalPtRecordResponse?.vob?.payer?.payerName ??
        vobResult?.payload?.vobBody?.payer?.payerName 

      hasCRMChanged = checkForChanges(
        adjustedFacility,  // PtRecord Facility if exists
        vobResult?.payload?.fpClientFacilityId, // Existing VOB Facility
        adjustedPayer, // PtRecord Payer if exists
        vobResult?.payload?.vobBody?.payer?.payorName // ?? vobResult?.payload?.vobBody?.payer?.facilityPayorName // Existing Payer Facility
      );

      handleChange = hasCRMChanged.facilityChange || hasCRMChanged.payerChange;
      isConverted = selectedIoc.workflow.workflowId === 3;

      const config: any = {
        paramId: paramId,
        masterListPayers: payers,
        facilityId: newFacilityId
      };
      // call GET all Facility Payers
      facilityPayersResponse = await dispatch(getEstimatorFacilityPayers(config));
    }
    
    let vobPayload: any = Utils.deepClone(vobResult?.payload?.vobBody);
    let updatedEstimate = Utils.deepClone(getAttachedEstimate.payload);

    if (handleChange && !isConverted) {
      let updatedIOC = Utils.deepClone(selectedIoc);
      let matchingPayer;
      //  Setting Payer ID based on Change Selection
      const newPayerName = hasCRMChanged.payerChange ?
        ptRecordClone?.vob?.payer?.payerName :
        vobResult?.payload?.vobBody?.payer?.payorName

      const newFacilityInfo = facilities
        ?.flat()
        .find(fac => fac?.facilityId === newFacilityId);

      if (hasCRMChanged.payerChange) {
        matchingPayer = facilityPayersResponse?.payload.find(
          (payer: any) => payer.facilityPayorName === newPayerName
        );
        // If there is no matching Payer in the Facility we need to default to Average OON
        if (!matchingPayer) {
          const newPayerToSave = facilityPayersResponse?.payload.find(
            (el: FacilityPayers) => el.cfgPayorId === 14
          )!;
          // check to see if Average OON exists / if it doesn't leave it blank
          if(newPayerToSave) {
            matchingPayer = newPayerToSave;
          }
        }
      }
    
      setChangeSummary({
        facilityChange: {
          hasChanged: hasCRMChanged.facilityChange,
          prevId: vobResult?.payload?.vobBody?.facility.facilityId,
          prevName: vobResult?.payload?.vobBody?.facility.facilityName,
          newId: newFacilityInfo?.facilityId!,
          newName: newFacilityInfo?.facilityName!
        },
        payerChange: {
          hasChanged: hasCRMChanged.payerChange,
          prevId: vobResult?.payload?.vobBody?.payer?.facilityPayorId,
          prevName: vobResult?.payload?.vobBody?.payer?.payorName,
          newId: matchingPayer?.facilityPayorId, 
          newName: matchingPayer?.facilityPayorName,
          ptRecordName: ptRecordClone?.vob?.payer?.payerName
        },
        clientId: savedClientId
      });

      // Open modal to confirm whether the user wants to save the detected changes
      const modalResult = await new Promise<ModalResponse>((resolve) => {
        setConfirmChangesModalOpen(true);
        setModalCallback(() => resolve);
      });

      // User triggered a change confirmation
      if(modalResult.confirmed){
        confirmPtChanges = true;
        // Setting Up Calls to make changes
        // Setting Facility we need to save 
        const facilityToSave = facilities
            ?.flat()
            .find(fac => fac?.facilityId === newFacilityInfo?.facilityId);

        // Only apply VOB mutations when we have an existing vobBody to update
        if (vobResult?.payload?.vobBody) {
          // The following changes need to be applied
          // in VOB
          // -- Changing Facility
          vobPayload.facility = {
            facilityId: facilityToSave?.facilityId,
            facilityName: facilityToSave?.facilityName,
          };
          // changing vob object to have payorId for the comparePayers utils below
          vobPayload.payer = {
            ...vobPayload.payer,
            payorId: hasCRMChanged.payerChange ? matchingPayer?.facilityPayorId : vobPayload.payer?.payorId,
            payorName: hasCRMChanged.payerChange ? matchingPayer?.facilityPayorName : vobPayload.payer?.payorName,
          };
          // -- Change Payer / Plan
          if (facilityPayersResponse?.payload) {
            const facilityPayers = [...(facilityPayersResponse.payload as FacilityPayers[])].sort((a, b) => a.facilityPayorId! > b.facilityPayorId! ? 1 : -1);
            let payerToSave: FacilityPayers | null;
            const matchedPayer: FacilityPayers | null = admissionsAdvisorUtils.compareFacilityPayorId(facilityPayers, vobPayload);

            // is there a matched Payor based on ptRecord change?
            if (matchedPayer) {
              payerToSave = matchedPayer;
            } else {
              // Setting default payer Avg OOO (14) if the payer has no match within the facility
              payerToSave = facilityPayers?.find(
                (el: FacilityPayers) => el.cfgPayorId === 14
              )!;
            }

            // This means there's no average out of network leaving blank
            if (!payerToSave) {
              vobPayload.payer = {...emptyVob.payer};
              vobPayload.plan = {...emptyVob.plan};
            } else {
              // we are setting the new payer
              vobPayload.payer = {
                payorId: payerToSave?.cfgPayorId,
                payorName: payerToSave?.facilityPayorName,
                payorCode: payerToSave?.facilityPayorCode,
                inNetwork: payerToSave?.inNetwork,
                payersList: undefined,
              };

              // setting correct plan name
              const newPlanName: string = originalPtRecordResponse?.vob?.plan?.planName;
              vobPayload.plan.planName = newPlanName ? newPlanName : vobPayload.plan.planName;

              let planToSave: FacilityPayerPlans | null;
              // if it had a previous plan we need match
              if (vobPayload.plan.planName) {
                planToSave = admissionsAdvisorUtils.comparePlansByName(payerToSave, vobPayload);
              } else {
                planToSave = payerToSave.facilityPayorPlans?.find(
                  (el: FacilityPayerPlans) => el.cfgPayorPlanId === 33
                )!;
              }

              if (!planToSave) {
                // if no plan 'ALL' is found, reset plan
                vobPayload.plan = {...emptyVob.plan};
              } else {
                // we are filling the VOB with the new plan
                vobPayload.plan = {
                  payorPlanId: planToSave?.cfgPayorPlanId,
                  planName: planToSave?.facilityPayorPlanName,
                  planCode: planToSave?.facilityPayorPlanCode,
                  facilityPlanId: planToSave?.facilityPayorPlanId,
                  pdrRate: (planToSave?.facilityPayorLocRateLos as PayorLocRateLos)
                    ?.pdrRate,
                  losDays: (planToSave?.facilityPayorLocRateLos as PayorLocRateLos)
                    ?.losDays,
                  payersPlanList: facilityPayers
                };
              }
            }
          }
        }
        // in Estimate
        updatedEstimate.facilityLevelOfCare = []; // Clear LOC
        updatedEstimate.facilityId = newFacilityId; // Change facilityId    
        // in IOC
        updatedIOC.facilityId = newFacilityId; // Change facilityId 
        updatedIOC.facilityName = newFacilityInfo?.facilityName;  // Change Facility Name 
      }
    }

    if (facilityId && ptRecordClone) {
      let vob: Vob = vobPayload;
      let payer = vob.payer || emptyVob.payer;
      let plan = vob.plan || emptyVob.plan;
      let client = emptyVob.client;
      let facility = emptyVob.facility;

      // if existing VOB is missing client information we need to build it
      if(vob?.client?.clientId) {
        client = vob.client;
      } else {
        const selectedClient = allClients?.find(client => client.clientId === Number(savedClientId));
        client = {
          clientId: savedClientId,
          clientName: selectedClient?.clientName!,
          clientList: []
        };
      }

      // if existing VOB is missing facility information we need to build it
      if(vob?.facility?.facilityId) {
        facility = vob.facility;
      } else {
        const selectedClient = allClients?.find(client => client.clientId === Number(savedClientId));
        const clientFacilities = selectedClient?.clientFacilities?.find(cfacility => cfacility.facilityId === facilityId);
        facility = {
          facilityId: clientFacilities?.facilityId,
          facilityName: clientFacilities?.facilityName!
        };
      }

      const patientInsurance = ptRecordClone.patientInsurance?.[0];

      // Only merge patient record VOB into existing VOB when the response included a vob object.
      // Otherwise we would overwrite existing VOB classification coinsurance with empty/default values.
      const hadVobInResponse = ptRecordClone.vob != null;

      if (hadVobInResponse) {
        // Need to update the ptRecord VOB - if it doesnt exist we need to add it
        if (!ptRecordClone.vob) {
          ptRecordClone.vob = {};
        }

        if (!Array.isArray(ptRecordClone.vob?.inNetwVobClassifications)) {
          ptRecordClone.vob.inNetwVobClassifications = [];
        }

        if (!Array.isArray(ptRecordClone.vob?.ooNetwVobClassifications)) {
          ptRecordClone.vob.ooNetwVobClassifications = [];
        }

        // 1. Map estimate classification IDs from getAttachedEstimate
        const estimateIds = Array.isArray(
            getAttachedEstimate?.payload?.facilityLevelOfCare
        )
            ? getAttachedEstimate.payload.facilityLevelOfCare
                  .map((item: any) => item.vobClassificationId)
                  .filter((id: any) => id !== undefined && id !== null)
            : [];

        // 2. Map classification IDs from ptRecord estimate LOCs
        const ptRecordEstimateIds = Array.isArray(ptRecordClone.estimate?.loc)
            ? ptRecordClone.estimate.loc
                  .map((item: any) => item.vobClassificationId)
                  .filter((id: any) => id !== undefined && id !== null)
            : [];

        // 3. Merge and dedupe all classification IDs
        const uniqueVOBClassificationIds = Array.from(
            new Set([...estimateIds, ...ptRecordEstimateIds])
        );
        const existingVobClassifications = payer.inNetwork
          ? ptRecordClone.vob.inNetwVobClassifications
          : ptRecordClone.vob.ooNetwVobClassifications;
        const newIdsToAdd = uniqueVOBClassificationIds.filter(
          (id) =>
            !existingVobClassifications.some(
              (obj: { vobClassificationId: any; }) => obj.vobClassificationId === id
            )
        );
        const newObjectsToAdd = newIdsToAdd.map((id) => ({ vobClassificationId: id }));

        // 4. Append to the correct classification list
        if (payer.inNetwork) {
          ptRecordClone.vob.inNetwVobClassifications.push(...newObjectsToAdd);
        } else {
          ptRecordClone.vob.ooNetwVobClassifications.push(...newObjectsToAdd);
        }

        const ptRecordVOB = admissionsAdvisorUtils.matchClassifications(ptRecordClone.vob, vobClassificationsState);
        vob = admissionsAdvisorUtils.mapVOBs(vob, ptRecordVOB, patientInsurance);
      }

      vob.patientNotes = formatVOBComments(ptRecordClone);

      if (
          ptRecordClone?.patient?.insurance?.insuranceType === 'self-pay' &&
          (!ptRecordClone?.vob || Object.keys(ptRecordClone.vob).length === 0)
      ) {
          const selfPayPayer = getSelfPayPayer(facilityPayers);
          if (selfPayPayer) {
              payer = {
                  ...selfPayPayer,
                  payorId: selfPayPayer.cfgPayorId,
                  payorName: selfPayPayer.facilityPayorName,
                  payorCode: selfPayPayer.facilityPayorCode,
                  inNetwork: selfPayPayer.inNetwork,
                  payersList: selfPayPayer.facilityPayorPlans,
              };
      
              plan = getPlanDetails(selfPayPayer, facilityPayers);
          }
      }

      vob.payer = payer;
      vob.plan = plan;
      vob.client = client;
      vob.facility = facility;
     
      // if conditions are met change payer to selfpay
      // if self pay, keep the vob as a self pay vob from 'createSeflPayVob'
      // @ts-ignore
      if(vob.payer.cfgPayorId === 97){
        vob = admissionsAdvisorUtils.createSelfPayVob(
          vobResult?.payload.advisorPatientId,
          vob,
          vobClassificationsState[0]
        ).vobBody;
      }

      vob.urlParams = combinedParams;
      vob.initialInboundPt = true;
      
      // Save VOB in FinPass if changes were made
      // Ready to Save the Estimate changes *if change was made in CRM
      if (handleChange && !isConverted && confirmPtChanges) {
        const vobPostPayload: VobPostBody = {
          advisorPatientId: vobResult?.payload.advisorPatientId,
          fpClientId: savedClientId,
          fpClientFacilityId: vob.facility.facilityId,
          vobBody: {
            ...vob
          },
        };
      
        await dispatch(saveVob(vobPostPayload));

        const putEstimateBody = admissionsAdvisorUtils.mapToNewEstimate(
          vobPayload.vobId!,
          savedClientId,
          newFacilityId,
          updatedEstimate,
          vobResult?.payload.advisorPatientId,
          instanceOfCare.entity.patientEncounterId
        );

        await dispatch(callNewEstimatePut({ estimate: putEstimateBody, estimateId: updatedEstimate.estimateId }));
      }
      // Always dispatch vob so UI (e.g. op=pay payment modal) can react to urlParams
      dispatch(setVOB(vob));

      const patientConfig: any = {
        paramId: paramId,
        patientId: vobResult?.payload.advisorPatientId || undefined,
      };

      let updatedState: vobPatientPostBody;
      const getPatientInfo = await dispatch(
        getPatientDemographics(instanceOfCare.entity.patientId)
      );
      const patientContact: any = getPatientInfo.payload;
  
      if (
          (!patientContact.patientDetails.contact.firstName &&
              !ptRecordClone.patient.contact.firstName) ||
          (!patientContact.patientDetails.contact.lastName &&
              !ptRecordClone.patient?.contact.lastName) ||
          (!patientContact.patientDetails.contact.birthDate &&
              !ptRecordClone.patient.contact.birthDate)
      ) {
          clearAAState();
          return handleGenericError(crmInboundErrorMessages.patientInfoError);
      }

      const prefilledBirthDate = ptRecordClone.patient?.contact?.birthDate || patientContact.patientDetails.contact.birthDate;
      const formattedDOB = moment.utc(prefilledBirthDate).format('YYYY-MM-DD');

      const newPatientState: vobPatientPostBody = {
        firstName: ptRecordClone.patient?.contact.firstName
          ? ptRecordClone.patient.contact.firstName
          : patientContact.patientDetails.contact.firstName,
        lastName:
          ptRecordClone.patient?.contact?.lastName ??
          patientContact?.patientDetails?.contact?.lastName,
        birthDate: formattedDOB,
        fpPatientId: instanceOfCare.entity.patientId,
        advisorPatientId:
          vobResult?.payload.advisorPatientId ||
          patientContact.patientDetails.advisorPatientId,
        middleName: ptRecordClone.patient?.contact.middleName
          ? ptRecordClone.patient.contact.middleName
          : patientContact.patientDetails.contact.middleName,
        advisorPatientBody: {
          email: ptRecordClone.patient?.contact.email
            ? ptRecordClone.patient.contact.email
            : patientContact.patientDetails.contact.email,
          primaryAddress: ptRecordClone.patient?.contact.primaryAddress
            ? ptRecordClone.patient.contact.primaryAddress
            : patientContact.patientDetails.contact.primaryAddress,
          phoneNumber: ptRecordClone.patient?.contact.primPhoneNum
            ? ptRecordClone.patient.contact.primPhoneNum
            : patientContact.patientDetails.contact.primPhoneNum
        },
      };
      if (patientConfig.patientId) {
        const getExistingVobPatient = await dispatch(
          getVobPatient(patientConfig)
        );
        updatedState = {
          ...getExistingVobPatient.payload,
          ...newPatientState,
        };
      } else {
        updatedState = {
          ...newPatientState,
        };
      }

      dispatch(setPatient({...updatedState}));

      dispatch(setSelectedEncounter(instanceOfCare.entity));

      // If a Change was detected on launch - we need to resave IOC
      if (handleChange && !isConverted && confirmPtChanges) {
        let instanceOfCareSave = {...instanceOfCare.entity};
        const vobPayloadFormat = {vob : vob}
        const iocConfig = mapToIOCModel(
          vobPayloadFormat,
          updatedEstimate,
          instanceOfCareSave.patientId,
          updatedEstimate.estimateId,
          ptRecordClone,
          queryParams
        );

        // Saving IOC Changes in FinPass
        await dispatch(createPatientIoc(iocConfig));
    }

      isStatePrefilled.current = true;

      if(!estimasteRoleStatus || formedParams.dst === 'fp') {
          redirectURLPath = `/specialist/dashboard/${instanceOfCare.entity.patientId}`;
      } else if(formedParams.dst === 'fa'){
        redirectURLPath = '/admissions-advisor'
      }
      navigate(redirectURLPath);
    }
  };

  function checkForChanges(
      currentVOBFacilityId: Number,
      currentPtRFacilityId: Number,
      currentPtRPayer: String,
      currentVOBPayer: String
  ): {facilityChange: boolean; payerChange: boolean} {
      const facilityChange =
          currentVOBFacilityId !== currentPtRFacilityId &&
          currentVOBFacilityId !== undefined &&
          currentPtRFacilityId !== undefined;
      const payerChange =
          currentVOBPayer !== currentPtRPayer &&
          currentVOBPayer !== undefined &&
          currentPtRPayer !== undefined;
      return {
          facilityChange,
          payerChange,
      };
  }

  function hasFinAdviorRole() {
      return checkPermissions(
          userProfile.userRole.userRolePages,
          RolePageNames.AdmissionsAdvisor,
          'VOB',
          false
      );
  }

  function hasEstimatesRole() {
    return checkUserRolePage(
        userProfile.userRole.userRolePages,
        RolePageNames.Estimates
    );
  }

  function hasIOCRole() {
      const hasUserRolePage = userProfile.userRole.userRolePages.some(
          (page: UserRolePage) => {
              return (
                  page?.sysRolePage?.rolePageName === RolePageNames.PatientRecords
              );
          }
      );
      return hasUserRolePage;
  }

  const loadExistingPatient = async (clientsPatientIOCIdFilter: number, redirectUrlPath: string) => {
      const instanceOfCare = await patientService.getPatientInstanceOfCare({
          patientId: 0,
          encounterId: clientsPatientIOCIdFilter,
      });
      const getPatientInfo: any = await dispatch(
          getPatientDemographics(instanceOfCare.entity.patientId)
      );
      // Checking to see if Patient Info contains an advisorPatientId
      if (getPatientInfo.payload.patientDetails?.advisorPatientId !== null) {
          // it does -- run set patient normally
          // If User has new Estimates Role the new functions need to be called
          const hasEstimatesRoleAction = hasEstimatesRole();
          if (hasEstimatesRoleAction) {
              const selectedVob: VobV3 = Utils.deepClone(vobState.vob);
              newPatientLaunch(
                  true,
                  getPatientInfo.payload.patientDetails,
                  ptRecordResponse,
                  queryParams,
                  vobState,
                  estimate,
                  selectedVob ?? {},
                  vobClassificationsState,
                  currentClientId,
                  payers,
                  userProfile,
                  clientsLeadId,
                  clientsAccountId,
                  navigate,
                  dispatch,
                  allClientsMap
              )
          } else {
              // User is missing Estimates Role, use FinAdvisor Functions Instead
              setNewPatientRecord(
                  true,
                  getPatientInfo.payload.patientDetails,
                  redirectUrlPath
              );
          }
      } else {
          // it does not -- must create advisor patient
          let updatedPatientDetails = Utils.deepClone(getPatientInfo.payload.patientDetails);
          const ptRecord = Utils.deepClone(ptRecordResponse);
          const patientState: vobPatientPostBody = mapToPatientState(
              getPatientInfo.payload.patientDetails,
              ptRecord
          );
          const aAPatientResponse: any = await dispatch(
              saveVobPatient(patientState)
          );
          // update Patient Details locally with new advisorPatientId
          updatedPatientDetails.advisorPatientId =
              aAPatientResponse.payload.advisorPatientId;
          // then update the existing FinPass patient with the advisorPatientId
          const finPassPatientPayload = mapToPatientViewModel(
              patientState,
              ptRecord
          );
          delete finPassPatientPayload.clientId;
          await dispatch(
              saveFinPassPatient({
                  patient: finPassPatientPayload,
                  advisorPatientId: aAPatientResponse.payload.advisorPatientId,
                  isAddNew: false,
                  patientId: getPatientInfo.payload.patientDetails?.patientId,
              })
          );
          // continue on to set patient
          // If User has new Estimates Role the new functions need to be called
          const hasEstimatesRoleAction = hasEstimatesRole();
          if (hasEstimatesRoleAction) {
              const selectedVob: VobV3 = Utils.deepClone(vobState.vob);
              newPatientLaunch(
                  true,
                  getPatientInfo.payload.patientDetails,
                  ptRecordResponse,
                  queryParams,
                  vobState,
                  estimate,
                  selectedVob ?? {},
                  vobClassificationsState,
                  currentClientId,
                  payers,
                  userProfile,
                  clientsLeadId,
                  clientsAccountId,
                  navigate,
                  dispatch,
                  allClientsMap
              )
          } else {
              // User is missing Estimates Role, use FinAdvisor Functions Instead
              setNewPatientRecord(true, updatedPatientDetails, redirectUrlPath);
          }
      }
  }

  useEffect(() => {
    const isDebugMode = window.location.href.includes("debug=");
    const query = window.location.search;
    const urlParams = new URLSearchParams(query);
    const formedParams = Object.fromEntries(urlParams);

    setQueryParams(query);
    setDebugMode(isDebugMode);
    setCurrentClientId(formedParams.clientId);

    if (!isDebugMode && !isLaunchInit.current && allClients.length > 0) {
      isLaunchInit.current = true;
      getPtRecordData(
        formedParams.clientsPatientIOCId,
        Number(formedParams.clientId)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allClients]);

  useEffect(() => {
    if (Object.keys(patientRecordInfo!).length > 0 && leadDataLoaded) {
      setClientsLeadId(patientRecordInfo?.clientsPatientIOCId);
      setClientsAccountdId(patientRecordInfo?.clientsPatientAccountId);
      setPtRecordResponse(patientRecordInfo!);
      setOriginalPtRecordResponse(patientRecordInfo)
      checkExistingIOC(patientRecordInfo!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientRecordInfo]);

  useEffect(() => {
      if (iOCLoaded && clientsPatientIOCIdFilter !== undefined) {
        let redirectUrlPath = "/admissions-advisor"
          switch (clientsPatientIOCIdFilter.length) {
              case 0:
                  checkExistingClientAccountId();
                  break;
              case 1:
                  const hasEstimatesRoleAction = hasEstimatesRole();
                    // If User has new Estimates Role the new functions need to be called
                    if (hasEstimatesRoleAction) {
                      // this is where the new Relaunch v3 function goes
                      patientRelaunch(
                        clientsPatientIOCIdFilter,
                        ptRecordResponse,
                        queryParams,
                        userProfile,
                        dispatch,
                        navigate,
                        payers,
                        currentClientId,
                        originalPtRecordResponse,
                        facilities,
                        emptyVobV3,
                        allClientsMap,
                        vobClassificationsState,
                        setChangeSummary,
                        setConfirmChangesModalOpen,
                        setModalCallback,
                        clientsLeadId,
                        clientsAccountId
                      )
                    } else {
                      getPatientEncounter(clientsPatientIOCIdFilter[0]);
                    }
                  break;
              default:
                  handleGenericError(crmInboundErrorMessages.clientPatientIOCId);
                  break;
          }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsPatientIOCIdFilter]);

  useEffect(() => {
      if (clientsPatientAccountIdFilter !== undefined) {
          const urlParams = new URLSearchParams(queryParams);
          const formedParams = Object.fromEntries(urlParams);
          const estimasteRoleStatus = hasFinAdviorRole();
          const iocRoleStatus = hasIOCRole();
          let redirectUrlPath = "/admissions-advisor"
          switch (clientsPatientAccountIdFilter.length) {
              case 0:
                  if (formedParams.op === 'pay') {
                      if (!estimasteRoleStatus) {
                          if (iocRoleStatus) {
                            redirectUrlPath = "specialistDashboard";
                          } else {
                            redirectUrlPath = "/accessdenied";
                          }
                      }
                  } 
                  if (formedParams.dst === 'fp'){
                    redirectUrlPath = "specialistDashboard"
                  } else if(formedParams.dst === 'fa'){
                    redirectUrlPath = "/admissions-advisor"
                  }
                  const hasEstimatesRoleAction = hasEstimatesRole();
                  // If User has new Estimates Role the new functions need to be called
                  if (hasEstimatesRoleAction) {
                      const selectedVob: VobV3 = Utils.deepClone(vobState.vob);
                      newPatientLaunch(
                          false,
                          undefined,
                          ptRecordResponse,
                          queryParams,
                          vobState,
                          estimate,
                          selectedVob ?? {},
                          vobClassificationsState,
                          currentClientId,
                          payers,
                          userProfile,
                          clientsLeadId,
                          clientsAccountId,
                          navigate,
                          dispatch,
                          allClientsMap
                      )
                  } else {
                      // User is missing Estimates Role, use FinAdvisor Functions Instead
                      setNewPatientRecord(false, undefined, redirectUrlPath);
                  }
                  break;
              default:
                  if (formedParams.op === "pay") {
                    if (!estimasteRoleStatus) {
                        if (iocRoleStatus) {
                            redirectUrlPath = "specialistDashboard";
                        } else {
                            redirectUrlPath = "/accessdenied";
                        }
                    }
                }
                if (formedParams.dst === 'fp'){
                  redirectUrlPath = "specialistDashboard"
                } else if(formedParams.dst === 'fa'){
                  redirectUrlPath = "/admissions-advisor"
                }
                loadExistingPatient(clientsPatientAccountIdFilter[0], redirectUrlPath);
                break;
          }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsPatientAccountIdFilter]);


  return (
    <>
      {debugMode ? (
        <Paper className={classes.splashStyle}>
          <Grid
            container
            spacing={1}
            direction="row"
            style={{ paddingTop: "1em", paddingLeft: "3em" }}
          >
            <QueryStringList urlParams={queryParams} />
          </Grid>
        </Paper>
      ) : (
        <Paper className={classes.splashStyle}>
          <Grid
            container
            spacing={1}
            direction="row"
            style={{ paddingTop: "1em", paddingLeft: "3em" }}
          >
            <MobilePaymentsLoader showLoader={true} customMessage={crmInboundErrorMessages.loadingMsg} />
          </Grid>
      </Paper>
      )}
      {confirmChangesModalOpen  && (
       <ConfirmChangesModal
          open={confirmChangesModalOpen}
          handleModalCancel={() => {
              setConfirmChangesModalOpen(false);
              modalCallback({confirmed: false});
          }}
          handleSave={async () => {
              setConfirmChangesModalOpen(false);
              modalCallback({confirmed: true});
          }}
          changes={changeSummary}
      />
      )}
    </>
  );
};
export default Launch;
